import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { getToken, getAuthUser, isAuthenticated } from "../auth";

const API = "https://api.vyapakprint.com/api";

const Profile = () => {
  const auth = useMemo(() => getAuthUser(), []);
  const token = useMemo(() => getToken(), []);

  const [formData, setFormData] = useState({
    businessName: "",
    contactPerson: "",
    country: "",
    state: "",
    city: "",
    pinCode: "",
    gstNumber: "",
    gstinScheme: "",
    shippingAddress: "",
    mobile: "",
    email: "",
  });

  const [distributionId, setDistributionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError("");
        setSuccess("");

        if (!token || !isAuthenticated()) {
          throw new Error("Please log in again.");
        }

        const storedUser = auth?.storedUser || null;

        const email = auth?.email || storedUser?.email || null;

        const safeDistributionId =
          auth?.distributionId ||
          auth?.authUserId ||
          storedUser?.distributionId ||
          storedUser?.id ||
          storedUser?._id ||
          null;

        if (storedUser) {
          setDistributionId(
            storedUser?.distributionId ||
              storedUser?.id ||
              storedUser?._id ||
              null,
          );

          setFormData((prev) => ({
            ...prev,
            businessName: storedUser?.businessName || "",
            contactPerson: storedUser?.contactPerson || "",
            mobile: storedUser?.mobile || "",
            email: storedUser?.email || "",
          }));
        }

        const response = await axios.get(`${API}/distribution/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const raw = response?.data;

        let list = [];
        if (Array.isArray(raw)) {
          list = raw;
        } else if (Array.isArray(raw?.data)) {
          list = raw.data;
        } else if (Array.isArray(raw?.distributions)) {
          list = raw.distributions;
        }

        const matchedUser =
          list.find(
            (item) =>
              safeDistributionId &&
              String(item?._id || item?.id) === String(safeDistributionId),
          ) ||
          list.find(
            (item) =>
              email &&
              String(item?.email || "").toLowerCase() ===
                String(email).toLowerCase(),
          ) ||
          storedUser;

        if (!matchedUser) {
          throw new Error("Distribution application not found.");
        }

        const normalizedUser = {
          id: matchedUser?._id || matchedUser?.id || null,
          _id: matchedUser?._id || matchedUser?.id || null,
          distributionId: matchedUser?._id || matchedUser?.id || null,
          businessName: matchedUser?.businessName || "",
          contactPerson: matchedUser?.contactPerson || "",
          country: matchedUser?.country || "",
          state: matchedUser?.state || "",
          city: matchedUser?.city || "",
          pinCode: matchedUser?.pinCode || "",
          gstNumber: matchedUser?.gstNumber || "",
          gstinScheme: matchedUser?.gstinScheme || "",
          shippingAddress: matchedUser?.shippingAddress || "",
          mobile: matchedUser?.mobile || "",
          email: matchedUser?.email || "",
          wallet: Number(matchedUser?.wallet || storedUser?.wallet || 0),
        };

        setDistributionId(normalizedUser.distributionId);

        setFormData({
          businessName: normalizedUser.businessName,
          contactPerson: normalizedUser.contactPerson,
          country: normalizedUser.country,
          state: normalizedUser.state,
          city: normalizedUser.city,
          pinCode: normalizedUser.pinCode,
          gstNumber: normalizedUser.gstNumber,
          gstinScheme: normalizedUser.gstinScheme,
          shippingAddress: normalizedUser.shippingAddress,
          mobile: normalizedUser.mobile,
          email: normalizedUser.email,
        });

        localStorage.setItem(
          "user",
          JSON.stringify({
            id: normalizedUser.id,
            _id: normalizedUser._id,
            distributionId: normalizedUser.distributionId,
            email: normalizedUser.email,
            businessName: normalizedUser.businessName,
            contactPerson: normalizedUser.contactPerson,
            mobile: normalizedUser.mobile,
            wallet: normalizedUser.wallet,
          }),
        );

        localStorage.setItem(
          "distributionUser",
          JSON.stringify({
            id: normalizedUser.id,
            _id: normalizedUser._id,
            distributionId: normalizedUser.distributionId,
            email: normalizedUser.email,
            businessName: normalizedUser.businessName,
            contactPerson: normalizedUser.contactPerson,
            mobile: normalizedUser.mobile,
            wallet: normalizedUser.wallet,
          }),
        );
      } catch (err) {
        console.error("Error fetching profile data:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load profile data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [auth, token]);

  const handleChange = (e) => {
    const { id, value } = e.target;

    setSuccess("");

    setFormData((prev) => ({
      ...prev,
      [id]:
        id === "mobile"
          ? value.replace(/\D/g, "").slice(0, 15)
          : id === "gstNumber"
            ? value.toUpperCase()
            : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      if (!token || !isAuthenticated()) {
        throw new Error("Please log in again.");
      }

      if (!distributionId) {
        throw new Error("User profile not found.");
      }

      const payload = {
        businessName: formData.businessName.trim(),
        contactPerson: formData.contactPerson.trim(),
        country: formData.country.trim(),
        state: formData.state.trim(),
        city: formData.city.trim(),
        pinCode: formData.pinCode.trim(),
        gstNumber: formData.gstNumber.trim(),
        gstinScheme: formData.gstinScheme.trim(),
        shippingAddress: formData.shippingAddress.trim(),
        mobile: formData.mobile.trim(),
        email: formData.email.trim().toLowerCase(),
      };

      const response = await axios.put(
        `${API}/distribution/${distributionId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const updated = response?.data?.data || response?.data || payload;

      const safeUser = {
        id: distributionId,
        _id: distributionId,
        distributionId,
        email: updated?.email || payload.email,
        businessName: updated?.businessName || payload.businessName,
        contactPerson: updated?.contactPerson || payload.contactPerson,
        mobile: updated?.mobile || payload.mobile,
        wallet: Number(
          updated?.wallet ||
            auth?.storedUser?.wallet ||
            JSON.parse(localStorage.getItem("distributionUser") || "{}")
              ?.wallet ||
            0,
        ),
      };

      localStorage.setItem("user", JSON.stringify(safeUser));
      localStorage.setItem("distributionUser", JSON.stringify(safeUser));

      setSuccess("Profile updated successfully.");
    } catch (err) {
      console.error("Error updating profile:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update profile",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return <div className="p-6 text-gray-700">Loading profile...</div>;
  }

  return (
    <div className="container mx-auto min-h-screen min-w-full p-4 md:p-6">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Edit Profile</h1>
      </div>

      {error ? (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 text-green-700">
          {success}
        </div>
      ) : null}

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-xl md:p-8">
        <form className="space-y-8" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            <div>
              <label
                htmlFor="businessName"
                className="block text-sm font-medium text-gray-700"
              >
                Business Name
              </label>
              <input
                id="businessName"
                type="text"
                value={formData.businessName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="contactPerson"
                className="block text-sm font-medium text-gray-700"
              >
                Contact Person
              </label>
              <input
                id="contactPerson"
                type="text"
                value={formData.contactPerson}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="country"
                className="block text-sm font-medium text-gray-700"
              >
                Country
              </label>
              <input
                id="country"
                type="text"
                value={formData.country}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="state"
                className="block text-sm font-medium text-gray-700"
              >
                State
              </label>
              <input
                id="state"
                type="text"
                value={formData.state}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="city"
                className="block text-sm font-medium text-gray-700"
              >
                City
              </label>
              <input
                id="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="pinCode"
                className="block text-sm font-medium text-gray-700"
              >
                Pin Code
              </label>
              <input
                id="pinCode"
                type="text"
                value={formData.pinCode}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="gstNumber"
                className="block text-sm font-medium text-gray-700"
              >
                GST Number
              </label>
              <input
                id="gstNumber"
                type="text"
                value={formData.gstNumber}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 uppercase text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="gstinScheme"
                className="block text-sm font-medium text-gray-700"
              >
                GSTIN Scheme
              </label>
              <input
                id="gstinScheme"
                type="text"
                value={formData.gstinScheme}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div className="md:col-span-2">
              <label
                htmlFor="shippingAddress"
                className="block text-sm font-medium text-gray-700"
              >
                Shipping Address
              </label>
              <textarea
                id="shippingAddress"
                rows={4}
                value={formData.shippingAddress}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="mobile"
                className="block text-sm font-medium text-gray-700"
              >
                Mobile
              </label>
              <input
                id="mobile"
                type="text"
                inputMode="numeric"
                value={formData.mobile}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-6 py-3 text-white shadow-md transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
